import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Container, Typography, Button, TextField, MenuItem,
  CircularProgress, Alert, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Tab, IconButton, Drawer,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Paper
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../api/api';
import PacienteForm from './PacienteForm';
import GestionCitasPaciente, { categoryLabels } from './GestionCitasPaciente';
import ConsultaPaciente from './ConsultaPaciente';
import RecetasPaciente from './RecetasPaciente';
import ControlPesoModal from './ControlPesoModal';
import AsignarRutaModal from './AsignarRutaModal';
import FicherosPacienteModal from './FicherosPacienteModal';
import PacienteAccionesBar from './PacienteAccionesBar';
import { DndContext, useDraggable } from '@dnd-kit/core';


const categories = Object.keys(categoryLabels).map((key) => ({
  id: key, label: categoryLabels[key].title, icon: categoryLabels[key].icon,
}));

// Normalizers
const makeNormalizer = (defaults) => (obj) => {
  if (!obj) return defaults;
  return Object.fromEntries(Object.keys(defaults).map((k) => [k, obj[k] ?? defaults[k]]));
};

const normalizeCliente = makeNormalizer({
  id_cliente: null, nombre_cliente: '', email_cliente: '',
  telefonos_cliente: '', id_sucursal: null, createdAt: null, updatedAt: null,
});
const normalizePaciente = makeNormalizer({
  id_paciente: null, nombre_paciente: '', id_raza: null, id_cliente: null,
  edad: '', caracter: '', pelo: '', ojos: '', habitat: '', tamano: '',
  tatuaje: '', chip: '', expediente: '', pedigri: '', observaciones: '', qr_code: '',
});
const normalizeRaza = makeNormalizer({ id_raza: null, nombre_raza: '', id_especie: null, habilitada: true });
const normalizeEspecie = makeNormalizer({ id_especie: null, nombre_especie: '' });

const CHIP_COLORS = { leve: '#4caf50', moderada: '#ff9800', grave: '#f44336', mortal: '#000000' };
const getChipColor = (gravedad) => CHIP_COLORS[gravedad?.toLowerCase()] ?? '#e0e0e0';

const EMPTY_CITA_COUNTS = { VAC: 0, DES: 0, PIP: 0, CIR: 0, BAN: 0, TER: 0 };

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

 // 1. Crea el componente arrastrable
const DraggablePacienteBox = ({ children, position }) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: 'draggable-paciente',
  });

  const style = {
    // Sumamos la posición guardada + el movimiento actual del arrastre
    transform: `translate3d(${position.x + (transform?.x || 0)}px, ${position.y + (transform?.y || 0)}px, 0)`,
    position: 'fixed',
    bottom: 24,
    left: 24,
    zIndex: 1300,
    cursor: 'grab',
    touchAction: 'none' // Importante para móviles
  };

  return (
    <Box ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </Box>
  );
};

const DatosClientes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [cliente, setCliente] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [razas, setRazas] = useState([]);
  const [especies, setEspecies] = useState([]);
  const [pacientesAlergias, setPacientesAlergias] = useState({});
  const [alergiasCatalog, setAlergiasCatalog] = useState([]);
  const [formData, setFormData] = useState({ nombre_cliente: '', email_cliente: '', telefonos_cliente: '', id_sucursal: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPacienteId, setSelectedPacienteId] = useState(null);
  const [showPacienteForm, setShowPacienteForm] = useState(false);
  const [citasForm, setCitasForm] = useState({ visible: false, tabCategoria: 'VAC' });
  const [consultaForm, setConsultaForm] = useState({ visible: false, id_cita: null });
  const [recetasForm, setRecetasForm] = useState({ visible: false, id_paciente: null, id_cita: null });
  const [openRutaModal, setOpenRutaModal] = useState(false);
  const [pacienteParaRuta, setPacienteParaRuta] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [citaCounts, setCitaCounts] = useState(EMPTY_CITA_COUNTS);
  const [mensajeBienvenida, setMensajeBienvenida] = useState(location.state?.mensaje || '');
  const [showControlPesoModal, setShowControlPesoModal] = useState(false);
  const [controlPesoToEdit, setControlPesoToEdit] = useState(null);
  const [selectedPacienteForControl, setSelectedPacienteForControl] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [pacienteAEliminar, setPacienteAEliminar] = useState(null);
  const [motivoBorrado, setMotivoBorrado] = useState('');
  const [position, setPosition] = useState({ x: 0, y: 0 });
  // Estados para el Modal de Expedientes Clínicos (Ficheros)
  const [modalFicherosOpen, setModalFicherosOpen] = useState(false);
  const [pacienteSeleccionadoFichero, setPacienteSeleccionadoFichero] = useState(null);

  const [openCalendarioModal, setOpenCalendarioModal] = useState(false);
  const [pacienteCalendarioId, setPacienteCalendarioId] = useState(null);


  const handleDragEnd = (event) => {
  const { delta } = event;
  setPosition((prev) => ({
    x: prev.x + delta.x,
    y: prev.y + delta.y,
  }));
};


  const handleAbrirCalendario = (pacienteId) => {
  if (pacienteId) {
    setPacienteCalendarioId(pacienteId);
    setOpenCalendarioModal(true);
  }
};
  // Agrega esto cerca de las otras variables
const sucursalNombre = sucursales.find(
  s => s.id_sucursal === parseInt(formData.id_sucursal)
)?.nombre_sucursal || 'Sin sucursal';

  const recargarPacientes = async () => {
    const res = await api.get(`/pacientes/por-cliente/${id}`);
    const normalized = (Array.isArray(res.data) ? res.data : [])
      .map(normalizePaciente).filter(p => p.id_paciente !== null)
      .sort((a, b) => a.id_paciente - b.id_paciente)
    setPacientes(normalized);
    return normalized;
  };

  const recargarAlergias = async (normalized) => {
    try {
      const res = await api.get('/alergias-pacientes/');
      setPacientesAlergias(normalized.reduce((acc, p) => {
        acc[p.id_paciente] = res.data.filter(a => a.id_paciente === p.id_paciente);
        return acc;
      }, {}));
    } catch { setPacientesAlergias({}); }
  };

  // Effects
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentUserId(payload.id_usuario || payload.usuario_id || payload.id || null);
      } catch (err) { console.error('Error al decodificar token:', err); }
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setLoading(true); setError(null);
        const clienteData = normalizeCliente((await api.get(`/clientes/${id}`)).data);
        if (!clienteData.id_cliente) throw new Error('Cliente no encontrado o ID inválido');

        setCliente(clienteData);
        setFormData({
          nombre_cliente: clienteData.nombre_cliente, email_cliente: clienteData.email_cliente,
          telefonos_cliente: clienteData.telefonos_cliente, id_sucursal: clienteData.id_sucursal || '',
        });

        const [pacientesRes, alergiasPacienteRes] = await Promise.all([
          api.get(`/pacientes/por-cliente/${id}`),
          api.get('/alergias-pacientes/').catch(() => ({ data: [] })),
        ]);
        const normalizedPacientes = (Array.isArray(pacientesRes.data) ? pacientesRes.data : [])
          .map(normalizePaciente).filter(p => p.id_paciente !== null)
          .sort((a, b) => a.id_paciente - b.id_paciente);
        setPacientes(normalizedPacientes);
        setPacientesAlergias(normalizedPacientes.reduce((acc, p) => {
          acc[p.id_paciente] = alergiasPacienteRes.data.filter(a => a.id_paciente === p.id_paciente);
          return acc;
        }, {}));

        const [sucursalesRes, razasRes, especiesRes, alergiasRes] = await Promise.all([
          api.get('/sucursales/').catch(() => ({ data: [] })),
          api.get('/razas/').catch(() => ({ data: [] })),
          api.get('/especies/').catch(() => ({ data: [] })),
          api.get('/alergias/').catch(() => ({ data: [] })),
        ]);
        setSucursales(Array.isArray(sucursalesRes.data) ? sucursalesRes.data : []);
        setRazas((Array.isArray(razasRes.data) ? razasRes.data : []).map(normalizeRaza).filter(r => r.id_raza));
        setEspecies((Array.isArray(especiesRes.data) ? especiesRes.data : []).map(normalizeEspecie).filter(e => e.id_especie));
        setAlergiasCatalog(Array.isArray(alergiasRes.data) ? alergiasRes.data : []);
      } catch (err) {
        setError('Error al cargar datos: ' + (err.response?.data?.detail || err.message));
      } finally { setLoading(false); }
    })();
  }, [id]);

  useEffect(() => {
    if (!selectedPacienteId) { setCitaCounts(EMPTY_CITA_COUNTS); return; }
    api.get(`/citas/count?id_paciente=${selectedPacienteId}`)
      .then(r => setCitaCounts(r.data))
      .catch(err => setError('Error al cargar conteos: ' + (err.response?.data?.detail || err.message)));
  }, [selectedPacienteId]);

    // Escuchar la tecla F2 para abrir el formulario de PacienteForm
  useEffect(() => {
    const handleKeyDownGlobal = (e) => {
      // Ignorar si el usuario está escribiendo en un input, textarea o select
      const isEditing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);
      if (isEditing) return;

      if (e.key === 'F2') {
        e.preventDefault(); // Previene la acción por defecto de F2 en el navegador
        setShowPacienteForm(true);
      }
    };

    window.addEventListener('keydown', handleKeyDownGlobal);
    return () => window.removeEventListener('keydown', handleKeyDownGlobal);
  }, []);

  // Handlers
  const handleSaveCliente = async () => {
    if (!formData.nombre_cliente.trim()) { setError('El nombre es obligatorio'); return; }
    if (formData.email_cliente && !isValidEmail(formData.email_cliente)) { setError('Email inválido'); return; }
    try {
      const updated = normalizeCliente((await api.put(`/clientes/${id}`, formData)).data);
      setCliente(updated);
      setFormData({ nombre_cliente: updated.nombre_cliente, email_cliente: updated.email_cliente,
        telefonos_cliente: updated.telefonos_cliente, id_sucursal: updated.id_sucursal || '' });
      setError(null);
      alert('Cliente actualizado exitosamente');
    } catch (err) { setError('Error al guardar cliente: ' + (err.response?.data?.detail || err.message)); }
  };

  const handlePacienteSave = async () => {
  setShowPacienteForm(false); // ya no tocamos selectedPacienteId
  const normalized = await recargarPacientes().catch(() => []);
  await recargarAlergias(normalized);
  };

  const handleAdministrarCitas = (pacienteId, tabCategoria) => {
    setSelectedPacienteId(pacienteId);
    setCitasForm({ visible: true, tabCategoria });
    setConsultaForm({ visible: false, id_cita: null });
    setRecetasForm({ visible: false, id_paciente: null, id_cita: null });
  };

  const handleRegistrarConsulta = (pacienteId, idCita) => {
    setSelectedPacienteId(pacienteId);
    setConsultaForm({ visible: true, id_cita: idCita });
    setCitasForm({ visible: false, tabCategoria: 'VAC' });
    setRecetasForm({ visible: false, id_paciente: null, id_cita: null });
  };

  const handleAdministrarRecetas = (pacienteId, idCita) => {
    setSelectedPacienteId(pacienteId);
    setRecetasForm({ visible: true, id_paciente: pacienteId, id_cita: idCita });
    setCitasForm({ visible: false, tabCategoria: 'VAC' });
    setConsultaForm({ visible: false, id_cita: null });
  };

  const handleAbrirControlPeso = (pacienteId) => {
    setSelectedPacienteForControl(pacienteId); setControlPesoToEdit(null); setShowControlPesoModal(true);
  };

  const handleControlPesoSave = async () => {
    setControlPesoToEdit(null);
    try { await recargarPacientes(); } catch (err) { console.error('Error al refrescar pacientes:', err); }
  };

  const confirmEliminarPaciente = async () => {
    if (!pacienteAEliminar || !motivoBorrado.trim()) return;
    setLoading(true);
    try {
      await api.delete(`/pacientes/${pacienteAEliminar}`, { params: { motivo: motivoBorrado } });
      setPacientes(prev => prev.filter(p => p.id_paciente !== pacienteAEliminar));
      setPacientesAlergias(prev => { const { [pacienteAEliminar]: _, ...rest } = prev; return rest; });
      setOpenDeleteModal(false); setMotivoBorrado('');
    } catch (err) { setError('Error al eliminar: ' + (err.response?.data?.detail || err.message)); }
    finally { setLoading(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 2 }}><Alert severity="error">{error}</Alert></Box>;
  if (!cliente?.id_cliente) return <Box sx={{ p: 2 }}><Alert severity="warning">Cliente no encontrado</Alert></Box>;

  const nombrePacienteSeleccionado = pacientes.find(p => p.id_paciente === selectedPacienteId)?.nombre_paciente || 'Desconocido';
  const pacienteSeleccionadoActual = pacientes.find(p => p.id_paciente === selectedPacienteId) || null;



  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box component="main" sx={{ flexGrow: 1, width: '100%', p: 2 }}>
        <Container maxWidth="lg" sx={{ p: 2, mt: 2, mb: 2 }}>

          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clientes')} sx={{ mr: 2 }}>
              Atras
            </Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <Typography variant="h5">Ficha Cliente</Typography>
              <Button variant="contained" onClick={() => navigate(`/clientes/${id}/edit`)}>
                Datos Cliente
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>

            {/* Panel Pacientes */}
                  
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '300px' }, pl: pacienteSeleccionadoActual ? { xs: 0, md: 5 } : 0, transition: 'padding-left 0.25s ease', }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                {mensajeBienvenida && (
                  <Alert severity="success" onClose={() => setMensajeBienvenida('')} sx={{ mb: 3 }}>
                    {mensajeBienvenida}
                  </Alert>
                )}
                <Typography variant="h6">Pacientes Registrados</Typography>
                <Button variant="contained" onClick={() => { setShowPacienteForm(true); setSelectedPacienteId(null); }}>
                  + (F2)
                </Button>
              </Box>

              {pacientes.length > 0 ? (
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                  }}
                >
                  <Box sx={{ maxHeight: '265px', overflowY: 'auto' }}>
                    <Table sx={{ minWidth: 200 }}>
                      <TableHead>
                        <TableRow>
                          {['Nombre', 'Raza'].map(h => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 600,
                                backgroundColor: '#f8fafc',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                color: 'text.secondary',
                                fontSize: '0.8rem',
                                letterSpacing: '0.02em',
                              }}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                      {pacientes.map((paciente) => {
                        const raza = razas.find(r => r.id_raza === paciente.id_raza);
                        const especie = especies.find(e => e.id_especie === raza?.id_especie);
                        const alergias = pacientesAlergias[paciente.id_paciente];
                        return (
                          <TableRow
                                key={paciente.id_paciente}
                                sx={{
                                  backgroundColor: selectedPacienteId === paciente.id_paciente
                                    ? 'rgba(12, 148, 23, 0.12)'  // azul suave
                                    : 'inherit',
                                  outline: selectedPacienteId === paciente.id_paciente
                                    ? '1px solid #37c98efd'
                                    : 'none',
                                  transition: 'background-color 0.2s',
                                }}
                              >
                            <TableCell onClick={() => { setShowPacienteForm(true); setSelectedPacienteId(paciente.id_paciente); }} sx={{ cursor: 'pointer' }}>
                              {paciente.nombre_paciente || 'N/A'}
                              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {alergias?.length > 0 ? alergias.map(a => (
                                  <Chip key={a.id_alergia_paciente} size="small"
                                    label={`${alergiasCatalog.find(c => c.id_alergia === a.id_alergia)?.descripcion || 'N/A'}-${a.gravedad || 'N/A'}`}
                                    sx={{ bgcolor: getChipColor(a.gravedad), color: '#fff' }} />
                                )) : <Chip label="Sin alergias" size="small" color="default" />}
                              </Box>
                            </TableCell>
                            <TableCell onClick={() => { setShowPacienteForm(true); setSelectedPacienteId(paciente.id_paciente); }} sx={{ cursor: 'pointer' }}>
                              {raza ? `${raza.nombre_raza || 'N/A Raza'} - ${especie?.nombre_especie || 'N/A Especie'}` : 'N/A'}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                    </Table>
                  </Box>
                </Paper>
              ) : (
                <Typography>No hay pacientes asociados a este cliente</Typography>
              )}

              {/* Botonera de acciones: reacciona al paciente seleccionado */}
              <PacienteAccionesBar
                paciente={pacienteSeleccionadoActual}
                categories={categories}
                onControlPeso={handleAbrirControlPeso}
                onAsignarRuta={(paciente) => { setPacienteParaRuta(paciente); setOpenRutaModal(true); }}
                onVerCitasCalendario={handleAbrirCalendario}
                onRegistrarConsulta={(pacienteId) => handleRegistrarConsulta(pacienteId, null)}
                onAdministrarRecetas={(pacienteId) => handleAdministrarRecetas(pacienteId, null)}
                onAdministrarCitas={handleAdministrarCitas}
                onExpedienteDigital={(paciente) => { setPacienteSeleccionadoFichero(paciente); setModalFicherosOpen(true); }}
                onEliminar={(pacienteId) => { setPacienteAEliminar(pacienteId); setOpenDeleteModal(true); }}
                onClearSelection={() => setSelectedPacienteId(null)}
              />

              {/* Boton flotante para vista rapida de paciente */}
              <DndContext onDragEnd={handleDragEnd}>
                {selectedPacienteId && (
                  <DraggablePacienteBox position={position}>
                    <Box sx={{
                      px: 2,
                      py: 1.5,
                      backgroundColor: '#3d3d3d',
                      color: 'white',
                      borderRadius: 2,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 1,
                      boxShadow: 4,
                      userSelect: 'none' // Evita seleccionar texto al arrastrar
                    }}>
                      🐾{' '}    {/* Nombre del pacinete colocalo a la par del icono patitas */}
                      <strong>
                        {pacientes.find(p => p.id_paciente === selectedPacienteId)?.nombre_paciente}
                      </strong>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPacienteId(null);
                        }}
                        sx={{ color: 'white', ml: 1, p: 0 }}
                      >
                        ✕
                      </IconButton>
                    </Box>
                  </DraggablePacienteBox>
                )}
              </DndContext>
            </Box>

            {/* Panel Detalles Cliente */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '300px' } }}>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                  bgcolor: '#fff',
                  p: 2.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '1.05rem',
                  }}
                >
                  Detalles del Cliente
                </Typography>

                {[
                  { label: 'Nombre', key: 'nombre_cliente' },
                  { label: 'Email', key: 'email_cliente' },
                  { label: 'Teléfonos', key: 'telefonos_cliente' },
                ].map(({ label, key }) => (
                  <TextField
                    key={key}
                    label={label}
                    fullWidth
                    size="small"
                    value={formData[key]}
                    onChange={e => setFormData({ ...formData, [key]: e.target.value })}
                    sx={{ mb: 2 }}
                  />
                ))}

                <TextField
                  select
                  label="Sucursal"
                  fullWidth
                  size="small"
                  value={formData.id_sucursal}
                  onChange={e => setFormData({ ...formData, id_sucursal: e.target.value })}
                  sx={{ mb: 2 }}
                >
                  {sucursales.length > 0
                    ? sucursales.map(s => (
                        <MenuItem key={s.id_sucursal} value={s.id_sucursal}>
                          {s.nombre_sucursal}
                        </MenuItem>
                      ))
                    : (
                      <MenuItem value="" disabled>
                        No hay sucursales disponibles
                      </MenuItem>
                    )}
                </TextField>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button variant="contained" onClick={handleSaveCliente}>
                    Edición rápida
                  </Button>
                </Box>
              </Paper>
            </Box>
            </Box>

          {/* Secciones condicionales */}
        <Box
          sx={{
            pl: pacienteSeleccionadoActual ? { xs: 0, md: 5 } : 0,
            transition: 'padding-left 0.25s ease',
          }}
        >
          {citasForm.visible && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>Administrar Citas para Paciente</Typography>
              <Tabs
                value={citasForm.tabCategoria}
                onChange={(_, v) => setCitasForm({ ...citasForm, tabCategoria: v })}
                sx={{ mb: 2 }}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map(cat => (
                  <Tab
                    key={cat.id}
                    label={`${cat.label} (${citaCounts[cat.id] || 0})`}
                    value={cat.id}
                    icon={cat.icon}
                    iconPosition="start"
                  />
                ))}
              </Tabs>
              <GestionCitasPaciente
                visible
                onClose={() => {
                  setCitasForm({ visible: false, tabCategoria: 'VAC' });
                  setSelectedPacienteId(null);
                }}
                id_paciente={selectedPacienteId}
                id_sucursal={formData.id_sucursal}
                tabCategoria={citasForm.tabCategoria}
                nombre_paciente={nombrePacienteSeleccionado}
              />
            </Box>
          )}

          {consultaForm.visible && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>Registrar Consulta para Paciente</Typography>
              <ConsultaPaciente
                visible
                onClose={() => {
                  setConsultaForm({ visible: false, id_cita: null });
                  setSelectedPacienteId(null);
                }}
                id_paciente={selectedPacienteId}
                id_cita={consultaForm.id_cita}
              />
            </Box>
          )}

          {recetasForm.visible && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>Administrar Recetas para Paciente</Typography>
              <RecetasPaciente
                visible
                onClose={() => setRecetasForm({ visible: false, id_paciente: null, id_cita: null })}
                id_paciente={selectedPacienteId}
                id_cita={recetasForm.id_cita}
              />
            </Box>
          )}
        </Box>
        </Container>
      </Box>

      {/* Modales */}

      {/* Modal de Expediente Digital (Ficheros en Volumen DO) */}
      {pacienteSeleccionadoFichero && (
          <FicherosPacienteModal
              open={modalFicherosOpen}
              onClose={() => {
                  setModalFicherosOpen(false);
                  setPacienteSeleccionadoFichero(null);
              }}
              pacienteId={pacienteSeleccionadoFichero.id_paciente}
              nombrePaciente={pacienteSeleccionadoFichero.nombre_paciente}
          />
      )}


      {openRutaModal && pacienteParaRuta?.qr_code && (
        <AsignarRutaModal open onClose={() => setOpenRutaModal(false)}
          qrCode={pacienteParaRuta.qr_code} nombrePaciente={pacienteParaRuta.nombre_paciente} />
      )}

      <ControlPesoModal visible={showControlPesoModal}
        onClose={() => { setShowControlPesoModal(false); setControlPesoToEdit(null); }}
        pacienteId={selectedPacienteForControl} controlPeso={controlPesoToEdit}
        onSave={handleControlPesoSave}
        nombrePaciente={pacientes.find(p => p.id_paciente === selectedPacienteForControl)?.nombre_paciente || ''}
        currentUserId={currentUserId} idSucursal={cliente.id_sucursal} />

      <Dialog open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>¿Deshabilitar Paciente?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Esta acción ocultará al paciente de las listas activas. Por favor, indica el motivo:
          </Typography>
          <TextField fullWidth multiline rows={3} label="Motivo de la baja"
            placeholder="Ej: Fallecimiento, Traslado de clínica, Error en registro..."
            value={motivoBorrado} onChange={e => setMotivoBorrado(e.target.value)} variant="outlined" />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDeleteModal(false)} color="inherit">Cancelar</Button>
          <Button onClick={confirmEliminarPaciente} color="error" variant="contained"
            disabled={!motivoBorrado.trim() || loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Eliminación'}
          </Button>
        </DialogActions>
      </Dialog>
     {/* Modal para Ver Citas / Calendario */}
<Dialog
  open={openCalendarioModal}
  onClose={() => setOpenCalendarioModal(false)}
  fullWidth
  maxWidth="xl" // Usamos 'xl' para darle un espacio cómodo al calendario
>
  <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography variant="h6">
      Calendario de Citas - {pacientes.find(p => p.id_paciente === pacienteCalendarioId)?.nombre_paciente}
    </Typography>
    <IconButton onClick={() => setOpenCalendarioModal(false)}>
      ✕
    </IconButton>
  </DialogTitle>
  <DialogContent dividers sx={{ p: 0, height: '75vh' }}>
    {openCalendarioModal && pacienteCalendarioId && (
      <iframe
        src={`/citas/calendario/${pacienteCalendarioId}`}
        title="Calendario de Citas"
        width="100%"
        height="100%"
        style={{ border: 'none' }}
      />
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setOpenCalendarioModal(false)} color="inherit">
      Cerrar
    </Button>
  </DialogActions>
</Dialog>

      <Drawer anchor="right" open={showPacienteForm} onClose={() => setShowPacienteForm(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 800 }, p: 2 }}>
          <PacienteForm visible={showPacienteForm}
            onClose={() => setShowPacienteForm(false)}
            pacienteId={selectedPacienteId} onSave={handlePacienteSave}
            id_sucursal={formData.id_sucursal} id_cliente={id} razas={razas} especies={especies} sucursalNombre={sucursalNombre}/>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DatosClientes;